import { lazy, Suspense } from "react";
import { createBrowserRouter } from "react-router-dom";
import { Layout, ProtectedPages, UnprotectedPages, Loader, ErrorBoundary } from "components";
import { SignInPage } from "pages";

interface LazyRouteProps {
  children: React.ReactNode;
}

// Lazy load pages - need to handle named exports properly
const SignUpPage = lazy(() =>
 import("pages").then((m) => ({ default: m.SignUpPage })),
);
const PhoneNumberPage = lazy(() =>
 import("pages").then((m) => ({ default: m.PhoneNumberPage })),
);
const ForgotPasswordPage = lazy(() =>
 import("pages").then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
 import("pages").then((m) => ({ default: m.ResetPasswordPage })),
);
const VerifyEmailPage = lazy(() =>
 import("pages").then((m) => ({ default: m.VerifyEmailPage })),
);
const AuthActionPage = lazy(() =>
 import("pages/auth-action").then((m) => ({ default: m.AuthActionPage })),
);
const ProfilePage = lazy(() =>
 import("pages").then((m) => ({ default: m.ProfilePage })),
);
const SettingsPage = lazy(() =>
 import("pages").then((m) => ({ default: m.SettingsPage })),
);
const NotFoundPage = lazy(() =>
 import("pages").then((m) => ({ default: m.NotFoundPage })),
);

// Wrapper component for lazy loaded routes with ErrorBoundary
const LazyRoute = ({ children }: LazyRouteProps): React.ReactElement => (
 <ErrorBoundary>
    <Suspense fallback={<Loader loadingMessage="Loading.." />}>
   {children}
  </Suspense>
 </ErrorBoundary>
);

export const router = createBrowserRouter([
 {
  element: <Layout />,
  children: [
   // Auth pages (only when signed OUT) - handles root redirect for unauthenticated
   {
    path: "/",
    element: <UnprotectedPages />,
    children: [
     { 
      path: "sign-in", 
      element: (
       <ErrorBoundary>
        <SignInPage />
       </ErrorBoundary>
      )
     }, // Eager load - main entry point
     {
      path: "sign-up",
      element: (
       <LazyRoute>
        <SignUpPage />
       </LazyRoute>
      ),
     },
     {
      path: "sign-up/phone-number",
      element: (
       <LazyRoute>
        <PhoneNumberPage />
       </LazyRoute>
      ),
     },
     {
      path: "forgot-password",
      element: (
       <LazyRoute>
        <ForgotPasswordPage />
       </LazyRoute>
      ),
     },
     {
      path: "reset-password",
      element: (
       <LazyRoute>
        <ResetPasswordPage />
       </LazyRoute>
      ),
     },
     {
      path: "verify-email",
      element: (
       <LazyRoute>
        <VerifyEmailPage />
       </LazyRoute>
      ),
     },
     {
      path: "auth-action",
      element: (
       <LazyRoute>
        <AuthActionPage />
       </LazyRoute>
      ),
     },
    ],
   },

   // App pages (only when signed IN) - handles root redirect for authenticated
   {
    path: "/",
    element: <ProtectedPages />,
    children: [
     {
      path: "profile",
      element: (
       <LazyRoute>
        <ProfilePage />
       </LazyRoute>
      ),
     },
     {
      path: "settings",
      element: (
       <LazyRoute>
        <SettingsPage />
       </LazyRoute>
      ),
     },
     {
      path: "settings/reset-password",
      element: (
       <LazyRoute>
        <ResetPasswordPage />
       </LazyRoute>
      ),
     },
     {
      path: "settings/phone-number",
      element: (
       <LazyRoute>
        <PhoneNumberPage />
       </LazyRoute>
      ),
     },
    ],
   },
  ],
 },
 {
  path: "*",
  element: (
   <LazyRoute>
    <NotFoundPage />
   </LazyRoute>
  ),
 },
]);

// TODO: Add better loading skeleton components for auth pages